import { useState, useRef, useEffect, useCallback, useMemo } from 'preact/hooks';
import { useSpeechRecognition } from '../hooks/useSpeechRecognition';
import { useRecorderDock } from '../hooks/useRecorderDock';
import { haptic } from '../utils/haptic';
import { executeQuote } from '../shared-ui/QuotePill';
import { PTTButton } from './PTTButton';
import {
  MicIcon,
  MicBlockedIcon,
  AttachIcon,
  CatchupIcon,
  SendIcon,
  CancelIcon,
} from './ComposerIcons';
import type { VoiceState } from '../utils/voiceState';
import { voiceStateLabel } from '../utils/voiceState';
import { ConfirmAttachmentModal } from './ConfirmAttachmentModal';
import { useSettingsStore } from '../state/settings.store';
import { useComposerStore, useIsBusy, useSendFailed } from '../state/composerStore';
import {
  PTT_HOLD_START,
  PTT_HOLD_END,
  PENDING_HOLD_STOP_WINDOW_MS,
  decideHoldStart,
  decideHoldEnd,
} from '../utils/holdToTalk';
import {
  preempt as coachPreempt,
  dismiss as coachDismiss,
  offer as coachOffer,
  evaluate as coachEvaluate,
  markDiscovered as coachMarkDiscovered,
  coachAnchorSelector,
} from '../state/coachStore';

// Computed once at module scope — stable across renders
const isMobile = /Android|iPhone|iPad/i.test(navigator.userAgent);

// Pasted text length that triggers full-viewport expansion (Phase 4)
const EXPAND_PASTE_THRESHOLD = 150;

/** The product's "catch me up" context-refresh prompt. Exported for the desktop home's
 *  guided starters (desktopHomeStarters.ts re-uses it verbatim — SE-03 pins the lockstep). */
export const CATCHUP_PROMPT =
  'Refresh your context on this session: check the recent messages, any in-progress git work, and open issues. ' +
  'Then give me a quick summary — what was done, what is still pending, and any blockers or open issues I should know about.';

/** Available @mention targets — keep in sync with backend voice.gateway.ts routing */
const MENTION_OPTIONS = [
  { keyword: '@gemini', label: 'Gemini', desc: 'Google Gemini consultation', icon: 'G', iconCls: 'google' },
  { keyword: '@opus', label: 'Opus', desc: 'Claude Opus escalation', icon: 'O', iconCls: 'anthropic' },
  { keyword: '@haiku', label: 'Haiku', desc: 'Claude Haiku (fast, cheap)', icon: 'H', iconCls: 'anthropic' },
  { keyword: '@sonnet', label: 'Sonnet', desc: 'Claude Sonnet (default agent)', icon: 'S', iconCls: 'anthropic' },
  { keyword: '@ollama', label: 'Ollama', desc: 'Local Ollama model', icon: 'L', iconCls: 'local' },
  { keyword: '@imagen', label: 'Imagen', desc: 'Image generation (Imagen 4)', icon: '🎨', iconCls: 'media' },
  { keyword: '@veo', label: 'Veo 2', desc: 'Video generation (Veo 2)', icon: '🎬', iconCls: 'media' },
  { keyword: '@veo3', label: 'Veo 3', desc: 'Video + audio generation (Veo 3)', icon: '🎬', iconCls: 'media' },
] as const;

interface Props {
  /** Voice-path callers pass originSessionId so the message lands in the session
   *  the operator was on when they tapped the mic, not whichever tab they
   *  swiped to during transcription. */
  onSendText: (text: string, originSessionId?: string | null) => void;
  onAttachTap?: (() => void) | undefined;
  hasAttachments: boolean;
  onCancel?: (() => void) | undefined;
  placeholder?: string;
  /** When true, disables autocorrect/autocapitalize for terminal-style input */
  terminalMode?: boolean;
  /** BCP-47 language tag passed to Web Speech API (e.g. 'bg-BG') */
  speechLang?: string;
  /** Whisper transcription — used when Web Speech API is unsupported or fails */
  transcribeAudio?: (chunks: Blob[], onResult: (text: string) => void, alreadyStreamed?: boolean, recordingId?: string, originSessionId?: string | null) => void;
  /** Session active at the moment the mic is tapped. Captured into a ref by
   *  InputBar so the eventual transcription + auto-submit route to this
   *  session, not whatever tab the operator swiped to during recording. */
  currentSessionId?: string | null;
  /** Streams a single 250ms chunk to the backend during recording (incremental upload).
   *  Returns true if the chunk was sent, false if it was dropped (socket not connected). */
  sendAudioChunk?: (chunk: Blob, recordingId?: string, index?: number) => Promise<boolean>;
  /** Long-press mic to toggle STT language */
  onToggleLang?: () => void;
  /** When true, always use server-side Whisper instead of the Web Speech API */
  preferWhisper?: boolean;
  // settings-zustand-migration Phase 4: autoSubmitVoice removed from props —
  // InputBar now reads it from useSettingsStore directly.
  /** When true, the in-row paperclip button is suppressed. The parent composer
   *  (e.g. BottomComposer's `+` drawer) takes over attach affordance. */
  hideAttach?: boolean;
  /** Gemini master switch from `GET /profile` features.gemini_enabled. When
   *  false (the protofac/valko profiles), the `@gemini` mention is hidden and
   *  dropped from the placeholder. Default true (Gemini on, e.g. dogfood). */
  geminiEnabled?: boolean;
  /** Desktop composer voice chrome (lane va-ui-composer-fsm-2026-10-01, issue #2204):
   *  the machine state this composer renders — derived ONCE by DesktopShell via
   *  utils/voiceState.ts (the SAME derivation the home hero consumes, extracted —
   *  never a parallel copy) and threaded in as a render prop. When present, the mic
   *  control renders the inline state machine (data-voice-state + ring + bars + the
   *  visible state label) and the row's icon buttons render real SVG icons. DesktopShell
   *  passes it; MobileShell leaves it undefined → the shared DOM on mobile is
   *  byte-identical to pre-lane (the frozen mobile-ux suite). PURE RENDERER: no state,
   *  no behavior — the recorder/busy/TTS guards all stay exactly where the machine
   *  owns them. */
  voiceState?: VoiceState;
}

export function InputBar({ onSendText, onAttachTap, hasAttachments, onCancel, placeholder, terminalMode, speechLang, transcribeAudio, sendAudioChunk, onToggleLang, preferWhisper, hideAttach, geminiEnabled = true, currentSessionId, voiceState }: Props) {
  // busy-to-outbound-migration Phase 1: subscribe directly to per-session
  // busy state from composerStore rather than receiving it as a prop. This
  // makes InputBar a renderer of machine state, not an owner of someone
  // else's flag — recomputes correctly when outbound[].mode transitions
  // for THIS session, no parent prop-drilling required.
  const busy = useIsBusy(currentSessionId ?? null);
  // CS-3 (plan §9.3): the reason of a `send_failed` outbound entry when the session is
  // otherwise idle, else null. Drives the minimal user-visible "turn failed" notice — a
  // send_failed (incl. the new stream_stalled) used to un-stick the composer SILENTLY.
  // Called unconditionally (rules-of-hooks); the terminal composer suppresses it at render.
  const sendFailedReason = useSendFailed(currentSessionId ?? null);
  // Phase 4: autoSubmitVoice from store.
  const autoSubmitVoice = useSettingsStore((s) => s.autoSubmitVoice === true);
  const { supported: phoneSupported, listening: phoneListening, micPermission, start: startPhone, stop: stopPhone } = useSpeechRecognition();
  const [whisperTranscribing, setWhisperTranscribing] = useState(false);
  const [micError, setMicError] = useState<string | null>(null);

  // Mirror micError into the coach substrate (Phase 3b, kind:'toast' → preempt — an
  // error must win the slot immediately, requeuing whatever coach was showing). Local
  // `micError` state stays the single source of truth (it ALSO drives PTTButton's
  // inline `.ptt-hint--error` span — a distinct, permanent recording-dock rendering,
  // not just the floating toast), so this only mirrors; it doesn't own the message.
  // ttlMs:8000 is a backstop only — the normal path is still the per-call-site
  // setTimeout(()=>setMicError(null)) firing and hitting this effect's else-branch.
  // Without the backstop, a second error arriving before the first clears (preempt
  // requeues the stale one, no timer follows it) or an InputBar unmount mid-error
  // (BottomComposer remounts per session — the pending setTimeout fires on nothing)
  // would leave the toast stuck until the next unrelated preempt/dismiss.
  useEffect(() => {
    if (micError) {
      coachPreempt({ id: 'mic-error', kind: 'toast', body: micError, role: 'alert', ttlMs: 8000 });
    } else {
      coachDismiss('mic-error');
    }
  }, [micError]);

  // Whisper is the default path. Web Speech is only used when the user explicitly
  // opts out (preferWhisper === false) AND is on desktop. On mobile, always Whisper.
  const useWhisperPath = !!(transcribeAudio && (isMobile || preferWhisper !== false));

  const dictationBaseRef = useRef('');

  // Fallback forceWhisper state for desktop Web-Speech-failure path
  const [forceWhisper, setForceWhisper] = useState(false);

  // inputbar-text-shadow-migration Phase 1+2: `text` reads directly from
  // composerStore (single source of truth, keyed by currentSessionId).
  // `setText` is a thin write-through to composerStore.setDraft — the SOLE
  // write path. Phase 2 removed the parent `onDraftChange` callback wiring;
  // there is no longer any duplicate-write through App.tsx's handleDraftChange.
  //
  // Phase 3 (this file's `originDraft` selector below): the auto-submit
  // race-handling `[text]` effect now keys off the ORIGIN session's draft
  // (via `originSessionIdRef.current`), not the active tab's draft. Required
  // because the TRANSCRIPTION wire-handler routes to the origin session's
  // draft — when the user swipes during the transcription gap, the active
  // tab's draft is irrelevant.
  const text = useComposerStore((s) => (currentSessionId ? s.sessions.get(currentSessionId)?.input.draft ?? '' : ''));
  const setText = useCallback((v: string) => {
    if (currentSessionId) {
      useComposerStore.getState().setDraft(currentSessionId, v);
    }
  }, [currentSessionId]);
  const textRef = useRef(text);
  textRef.current = text;
  // Mic ownership across sessions: composerStore.globalRecording pins to the
  // origin sessionId for the whole recording window. When it points at a
  // DIFFERENT session than the one rendered here, this session's mic is "held
  // elsewhere" — typing stays available, but a second recording must not start.
  // Same signal the purple session dot uses (agentListStore.ts). Clears the
  // instant the holder's recording ends (AUDIO_END releases the slot), so the
  // mic returns to normal with no extra wiring. See
  // plans/composer-pipeline-busy-render.md.
  const globalRecordingSessionId = useComposerStore((s) => s.globalRecording.sessionId);
  const micHeldElsewhere = !!globalRecordingSessionId && globalRecordingSessionId !== currentSessionId;
  const inputRef = useRef<HTMLInputElement>(null);
  const taRef = useRef<HTMLTextAreaElement>(null);
  // Preserve textarea scroll position across Preact re-renders (socket events
  // like SESSION_HEALTH_DATA cause re-renders that reset scrollTop to 0)
  const micLongPressTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const micIsLongPress = useRef(false);

  /** Resolve the BottomComposer collapsed-mode textarea cap from the CSS
   *  variable so JS and CSS share a single source of truth (#1138). Reads
   *  the cap from the textarea's computed `max-height` — works for both
   *  fixed px and `min(40vh, 240px)` thanks to getComputedStyle resolving
   *  the keyword. Falls back to 240 if unreadable (e.g. test env). */
  const resolveComposerMaxHeight = (el: HTMLElement): number => {
    const cs = window.getComputedStyle(el).maxHeight;
    const px = parseFloat(cs);
    return Number.isFinite(px) && px > 0 ? px : 240;
  };

  // Empty-text + attachment send guard (issue #1137, supersedes the
  // window.confirm() introduced by PR #1135). Open state plus the captured
  // payload so that the user's choice (Cancel / Send) resolves correctly even
  // if `text` mutates between opening the modal and the user responding.
  const [confirmAttachOpen, setConfirmAttachOpen] = useState(false);
  const pendingAttachSendRef = useRef<string>('');

  const autoResizeTa = () => {
    const el = taRef.current;
    if (!el) return;
    // In expanded mode CSS flex controls height — clear any inline height so flex works.
    if (el.closest('.bottom-composer--expanded')) {
      el.style.height = '';
      return;
    }
    // Inside BottomComposer (collapsed), the CSS variable
    // `--composer-textarea-max-h` is the single source of truth for the cap.
    // Honor it here so inline `height: scrollHeight` doesn't blow past it
    // (inline style overrides CSS max-height in the height property).
    if (el.closest('.bottom-composer')) {
      el.style.height = 'auto';
      el.style.height = `${Math.min(el.scrollHeight, resolveComposerMaxHeight(el))}px`;
      return;
    }
    el.style.height = 'auto';
    el.style.height = `${el.scrollHeight}px`;
  };

  const [composerFocused, setComposerFocused] = useState(false);

  // PTT discovery timer (coach-tooltip-system.md Phase 4, §5.2): "Hold the mic to
  // talk instead of typing" — reachable after 12s of composer focused+empty (a
  // struggle signal). Scoped entirely to this component: only this one catalog row
  // needs elapsed-time tracking, so a dedicated interval here is simpler than
  // threading a live focus-timer through the shared useChatShell snapshot builder.
  // The tick's snapshot zeroes every OTHER field, so only 'ptt' can possibly become
  // eligible from it — every other row's reachableWhen reads false/0, and the
  // anchor gate additionally excludes any other element-anchored row since only
  // `[data-coach-id=mic]` is declared mounted here.
  useEffect(() => {
    if (!composerFocused || text.trim().length > 0) return;
    coachOffer('ptt');
    const start = Date.now();
    const tick = () => {
      coachEvaluate({
        chatViewEntries: 0,
        swipeableAgents: 0,
        fabVisible: false,
        newSessionsCreated: 0,
        assistantMsgCount: 0,
        composerFocusedEmptyMs: Date.now() - start,
        drawerOpenedCount: 0,
        sessionCount: 0,
        mountedAnchors: [coachAnchorSelector('mic')],
      });
    };
    const t = setInterval(tick, 2000);
    return () => clearInterval(t);
  }, [composerFocused, text]);

  // Track whether incremental streaming is viable for the current recording.
  // Set to true when: (a) sendAudioChunk returns false (socket drop), OR
  // (b) the first chunk fails the EBML/WAV magic-byte check (mobile Chrome quirk
  // where MediaRecorder occasionally omits the initialization segment from chunk 0,
  // causing Whisper to reject the assembled buffer).  When true, fall back to the
  // combined-blob path (transcribeAudio with alreadyStreamed=false) which emits
  // AUDIO_RESET first to discard any stale partial chunks on the server.
  const droppedChunkRef = useRef(false);
  // Track in-flight sendAudioChunk promises so handleWhisperRecorded can await them
  // before emitting AUDIO_END. Without this, the async arrayBuffer() call inside
  // sendAudioChunk races with AUDIO_END: onstop fires synchronously after ondataavailable,
  // so AUDIO_END is emitted before the last chunk's socket.emit completes. The late chunk
  // then lands in the backend buffer AFTER AUDIO_END cleared it, corrupting the next
  // recording's buffer with a non-EBML-header continuation segment → magic-check failure
  // that persists across all subsequent recordings until page reload.
  const pendingChunkSendsRef = useRef<Promise<boolean>[]>([]);
  /** Stable ref to the current recording's UUID — updated in onChunk so it is
   *  always current when sendAudioChunk is called. */
  const currentRecordingIdRef = useRef<string | undefined>(undefined);
  /** Session active the moment the mic was tapped. Pinned here so the eventual
   *  transcription + auto-submit route to this session, even if the user
   *  swiped to a different tab between start and stop (or talked through a
   *  silence-driven auto-send where there is no STOP press at all). */
  const originSessionIdRef = useRef<string | null>(null);
  /** Whisper auto-submit handshake: handleWhisperRecorded sets this true after
   *  setText(newText); the [text]-watching useEffect below the auto-submit
   *  listener consumes it. Race-safe replacement for the old rAF+dispatchEvent.
   *
   *  Tab-switch survival (issue #1139): if the user backgrounds the tab between
   *  recorder.stop() and TRANSCRIPTION arrival, the [text]-effect may still flush
   *  (Preact commits synchronously), but downstream rAF/microtask chains can stall
   *  in a hidden tab. We add THREE belt-and-suspenders:
   *    (a) microtask-scheduled direct submit after setText, capturing newText
   *        in a closure so the send chain doesn't depend on the [text] effect
   *    (b) visibilitychange listener that re-runs the pending submit on tab return
   *    (c) the existing [text]-watching useEffect (primary path on the happy path).
   *  All three converge on handleSendTextRef.current() which is idempotent for
   *  the auto-submit case because the ref-flag is cleared on first fire. */
  const pendingAutoSubmitVoiceRef = useRef(false);
  /** Captures the transcript text that should be auto-submitted. Used by both
   *  the microtask path and the visibilitychange recovery path so they send the
   *  *intended* text even if `text` state has not yet been committed. */
  const pendingAutoSubmitTextRef = useRef<string>('');

  /** Stable ref to onSendText prop so the auto-submit recovery path can fire
   *  without depending on the `text` state closure (which may be stale when the
   *  tab is hidden and renders/effects are batched). */
  const onSendTextRef = useRef(onSendText);
  onSendTextRef.current = onSendText;

  /** inputbar-text-shadow-migration Phase 3 — auto-submit handshake reads
   *  ORIGIN session's draft.
   *
   *  Before Phase 3, the `[text]`-watching useEffect at the bottom of this file
   *  consumed the active-tab's draft (`text = sessions.get(currentSessionId).
   *  input.draft`). After Phase 1+2, when the user swiped to a different tab
   *  during transcription, the active-tab's draft never updated (the
   *  TRANSCRIPTION wire-handler routes to the origin session's draft per
   *  Scenario 2 / `useSocket.ts:892`). The race-handling effect silently
   *  disengaged in the cross-tab case — leaving only the microtask path (a)
   *  and visibilitychange path (b) as backstops.
   *
   *  Phase 3 introduces `originDraft`, a selector keyed by the live origin pin
   *  in `originSessionIdRef.current`. The effect's dep array now references
   *  this value, so it fires when the wire-handler updates A's draft
   *  regardless of whether the user has swiped. Race-handling path (c)
   *  now correctly back-stops paths (a) and (b) in the cross-tab case too.
   *
   *  Key invariant: when origin == active tab, `originDraft === text` and the
   *  effect's behavior is unchanged. When they diverge, `originDraft` is the
   *  truthful source for the auto-submit. */
  const originDraft = useComposerStore((s) => {
    const sid = originSessionIdRef.current;
    if (!sid) return '';
    return s.sessions.get(sid)?.input.draft ?? '';
  });

  // Declared up here (instead of with the other mention state below) so the
  // tryAutoSubmitVoice useCallback can call setMentionActive without tripping
  // ESLint's no-use-before-define (TypeScript accepts the closure capture; the
  // lint rule is stricter — see DIFFICULTIES log entry for the recurring pattern).
  const [mentionActive, setMentionActive] = useState(false);

  /** Centralized auto-submit trigger. Idempotent — clears the ref-flag and only
   *  fires if there is pending intent. Used by all three convergence points
   *  (microtask path, [text] effect, visibilitychange listener). Bypasses the
   *  `handleSendText` closure (which reads `text` state and may be stale on
   *  hidden tabs) by calling `onSendText` directly with the captured pending
   *  text. Also performs the same composer-cleanup as handleSendText. */
  const tryAutoSubmitVoice = useCallback(() => {
    if (!pendingAutoSubmitVoiceRef.current) return;
    const pendingText = pendingAutoSubmitTextRef.current.trim();
    if (!pendingText) return;
    pendingAutoSubmitVoiceRef.current = false;
    pendingAutoSubmitTextRef.current = '';
    // EVENT-DRIVEN: route auto-submit to the session pinned at record-START,
    // not whichever tab the operator swiped to during transcription.
    onSendTextRef.current(pendingText, originSessionIdRef.current);
    // PR #1217 — clear the origin pin AFTER the send fires. Without this,
    // a stale value persists across subsequent (non-voice) sends and could
    // route a typed message to the wrong session if the next handleSendText
    // happens before a new record-START re-pins. The clear is here, not
    // earlier, so the onSendTextRef.current() call above has the correct value.
    originSessionIdRef.current = null;
    // Mirror handleSendText's composer cleanup so the UI state is consistent
    // regardless of which path fired the send.
    setText('');
    setMentionActive(false);
    if (taRef.current) {
      taRef.current.style.height = '';
    }
    setComposerFocused(false);
    document.dispatchEvent(new CustomEvent('composer-text-cleared'));
  }, []);

  const handleWhisperRecorded = useCallback(async (chunks: Blob[], recordingId?: string) => {
    if (!transcribeAudio) return;
    // Release the global mic slot on any STOP path (manual, hard-cap, silence
    // auto-stop) so OTHER session dots stop showing the purple "recording" indicator.
    useComposerStore.getState().setGlobalRecording(null, null);
    setWhisperTranscribing(true);
    // Drain all in-flight sendAudioChunk promises before sending AUDIO_END so the
    // backend always receives chunks in the correct order (AUDIO_CHUNK* then AUDIO_END).
    const pending = pendingChunkSendsRef.current;
    pendingChunkSendsRef.current = [];
    await Promise.all(pending);
    // Only use the incremental path if sendAudioChunk was active AND no chunk was dropped.
    const alreadyStreamed = !!sendAudioChunk && !droppedChunkRef.current;
    droppedChunkRef.current = false;
    transcribeAudio(chunks, (transcribed) => {
      setWhisperTranscribing(false);
      if (!transcribed) return;
      const base = dictationBaseRef.current;
      const newText = base ? base + ' ' + transcribed : transcribed;
      setText(newText);
      // PR #1217 — DROPPED the redundant onDraftChange?.(newText) call here.
      // The server's TRANSCRIPTION handler (useSocket.ts:892) already writes
      // the transcript to the ORIGIN session's draft via composerStore.setDraft.
      // Writing it AGAIN via onDraftChange would contaminate the ACTIVE tab's
      // draft when the operator swiped during transcription (cross-session bug
      // surfaced 2026-05-30).
      if (autoSubmitVoice) {
        // Race-safe trigger: setting a ref-flag here, consumed by a useEffect that
        // watches `text`. The effect fires AFTER the setText render commits AND
        // AFTER `handleSendTextRef.current` is updated to the new closure — so the
        // submit reads `newText`, not the stale empty string.
        // Old approach (rAF + dispatchEvent on 'auto-submit') raced with the
        // useEffect that refreshes handleSendTextRef; when rAF won, the listener
        // called the OLD handleSendText, hit `if (!val) return`, silent no-op.
        pendingAutoSubmitVoiceRef.current = true;
        pendingAutoSubmitTextRef.current = newText;
        // Tab-switch survival (issue #1139): schedule a secondary attempt via
        // queueMicrotask. If the tab is hidden, the [text] effect may stall but
        // microtasks still drain reliably on any subsequent JS execution turn.
        // tryAutoSubmitVoice() is idempotent — the [text] effect path will clear
        // the ref-flag first if it wins; this is a backstop.
        queueMicrotask(() => tryAutoSubmitVoice());
      } else {
        setComposerFocused(true);
        requestAnimationFrame(() => taRef.current?.focus());
      }
    }, alreadyStreamed, recordingId, originSessionIdRef.current);
  }, [transcribeAudio, sendAudioChunk, autoSubmitVoice, tryAutoSubmitVoice]);

  const onChunk = useMemo(() => {
    if (!sendAudioChunk) return undefined;
    // incrementalDisabled is scoped to the current recording; reset each useMemo.
    // When true, no more incremental chunks are sent — the fallback combined-blob
    // path takes over at stop time.
    let incrementalDisabled = false;
    let inspectedFirst = false;
    // Monotonic index of chunks ACTUALLY SENT (not inspected) — the first sent chunk
    // is index 0 so the server reassembles the staging buffer in order. Kept separate
    // from first-chunk inspection so an aborted/rejected first chunk never offsets it.
    let sentIndex = 0;
    return async (chunk: Blob) => {
      if (incrementalDisabled) return;
      if (!inspectedFirst) {
        inspectedFirst = true;
        // Validate that the first MediaRecorder chunk contains a recognized container
        // header.  On some mobile Chrome versions the EBML initialization segment is
        // absent from chunk 0, producing a buffer that Whisper immediately rejects.
        // Detect it here before sending anything to the server.
        // Read 8 bytes so we can detect MP4 (ftyp box at offset 4) in addition to
        // WebM (EBML magic at offset 0) and WAV (RIFF at offset 0).
        const header = await chunk.slice(0, 8).arrayBuffer();
        const v = new Uint8Array(header);
        const isWebM = v[0] === 0x1a && v[1] === 0x45 && v[2] === 0xdf && v[3] === 0xa3;
        const isWAV  = v[0] === 0x52 && v[1] === 0x49 && v[2] === 0x46 && v[3] === 0x46; // RIFF
        // MP4/M4A: ISO BMFF box — 4-byte size then "ftyp" at offset 4
        const isMP4  = v.length >= 8 && v[4] === 0x66 && v[5] === 0x74 && v[6] === 0x79 && v[7] === 0x70;
        // OGG: "OggS" at offset 0
        const isOGG  = v[0] === 0x4f && v[1] === 0x67 && v[2] === 0x67 && v[3] === 0x53;
        if (!isWebM && !isWAV && !isMP4 && !isOGG) {
          console.warn(
            `[audio] First chunk has unexpected magic bytes (${Array.from(v.slice(0,8)).map(b => b.toString(16).padStart(2,'0')).join(' ')}) — ` +
            'disabling incremental streaming, will use combined-blob fallback',
          );
          incrementalDisabled = true;
          droppedChunkRef.current = true;
          return;
        }
        // MP4 and OGG are valid but don't support incremental cluster streaming —
        // disable incremental for these formats and use combined-blob instead.
        if (isMP4 || isOGG) {
          incrementalDisabled = true;
          droppedChunkRef.current = true;
          return;
        }
      }
      // Pass the current recordingId (from the ref, synced below) + a monotonic index
      // so the server routes this chunk to the per-recording staging buffer and
      // reassembles in order. (Bug fix: recordingId was synced into the ref but never
      // actually passed here — every chunk emitted raw with no recordingId, so the
      // staging buffer was never populated. Incident 2026-06-14.)
      const idx = sentIndex++;
      const p = sendAudioChunk(chunk, currentRecordingIdRef.current, idx);
      pendingChunkSendsRef.current.push(p);
      p.then((sent) => {
        if (!sent) {
          incrementalDisabled = true;
          droppedChunkRef.current = true;
        }
      }).catch(() => {});
    };
  }, [sendAudioChunk]);

  const dock = useRecorderDock({
    onRecorded: handleWhisperRecorded,
    onCancel: () => {
      setMicError(null);
      useComposerStore.getState().setGlobalRecording(null, null);
    },
    onChunk,
  });
  // Keep currentRecordingIdRef in sync with dock.recordingId so the onChunk closure
  // (which runs during recording) always passes the correct UUID to sendAudioChunk.
  currentRecordingIdRef.current = dock.recordingId;

  // (mentionActive useState moved earlier — see comment above tryAutoSubmitVoice.)
  // Quote chip: shown when QuotePill detects a valid text selection
  const [quoteSelection, setQuoteSelection] = useState<{ text: string; sourceEl: Element } | null>(null);
  const [mentionFilter, setMentionFilter] = useState('');
  const [mentionIndex, setMentionIndex] = useState(0);

  const mentionMatches = useMemo(() => {
    if (!mentionActive) return [];
    const q = mentionFilter.toLowerCase();
    return MENTION_OPTIONS.filter((m) =>
      // Hide the Gemini affordance when the deployment disabled Gemini (protofac*/valko*).
      (geminiEnabled || m.keyword !== '@gemini') &&
      (m.keyword.toLowerCase().includes(q) || m.label.toLowerCase().includes(q))
    );
  }, [mentionActive, mentionFilter, geminiEnabled]);

  /** Detect @mention trigger from current text (only at start of input or after whitespace) */
  const updateMentionState = useCallback((val: string) => {
    // Check if cursor is right after an @ token at the start or after whitespace
    const match = val.match(/(?:^|\s)@(\S*)$/);
    if (match) {
      setMentionActive(true);
      setMentionFilter('@' + match[1]);
      setMentionIndex(0);
    } else {
      setMentionActive(false);
    }
  }, []);

  /** Complete the @mention by replacing the partial token */
  const completeMention = useCallback((keyword: string) => {
    const val = textRef.current;
    const match = val.match(/(?:^|\s)(@\S*)$/);
    if (match) {
      const before = val.slice(0, val.length - match[1].length);
      const newText = before + keyword + ' ';
      setText(newText);
      setMentionActive(false);
      requestAnimationFrame(() => taRef.current?.focus());
    }
  }, []);

  const adjustTextareaHeight = useCallback(() => {
    const el = taRef.current;
    if (!el) return;
    // In expanded mode CSS flex controls height — clear any inline override.
    if (el.closest('.bottom-composer--expanded')) {
      el.style.height = '';
      return;
    }
    el.style.height = '0px';
    // BottomComposer collapsed mode: cap uses the same CSS-variable-driven
    // resolver as autoResizeTa, so both paths stay consistent (#1138).
    // Outside BottomComposer, fall back to the legacy 36vh cap.
    const max = el.closest('.bottom-composer')
      ? resolveComposerMaxHeight(el)
      : Math.round(window.innerHeight * 0.36);
    const min = composerFocused ? 56 : 40;
    el.style.height = `${Math.min(Math.max(el.scrollHeight, min), max)}px`;
  }, [composerFocused]);

  // inputbar-text-shadow-migration Phase 1: the legacy prop-mirror useEffect
  // that copied the parent prop into local state on each change has been
  // deleted. With `text` now sourced from composerStore directly, the mirror
  // is no longer needed. The corresponding shape assertion lives in
  // SE-CSM-INPUTBAR-READS-STORE — see plans/inputbar-text-shadow-migration.md.

  // Focus the composer textarea + move cursor to end. View-layer signal (`focus-composer`)
  // dispatched by prefill producers AFTER they write the draft via the named store action
  // composerStore.appendOrReplaceDraft() (platform-isolation #1657 Phase 2b). This replaces
  // the old draft-carrying `prefill-input` CustomEvent bus: the append/replace draft write is
  // now state through the store (keyed by the explicit target session — no late-bound global
  // event), and this residual handler is the pure view concern (focus), which R-A2 permits.
  useEffect(() => {
    const handler = () => {
      requestAnimationFrame(() => {
        const el = taRef.current;
        if (!el) return;
        el.focus();
        el.selectionStart = el.value.length;
        el.selectionEnd = el.value.length;
      });
    };
    document.addEventListener('focus-composer', handler);
    return () => document.removeEventListener('focus-composer', handler);
  }, []);

  // Listen for quote-selection-change events from QuotePill to show/hide the fallback chip
  useEffect(() => {
    const handler = (e: Event) => {
      const detail = (e as CustomEvent).detail;
      setQuoteSelection(detail ? { text: detail.text, sourceEl: detail.sourceEl } : null);
    };
    document.addEventListener('quote-selection-change', handler);
    return () => document.removeEventListener('quote-selection-change', handler);
  }, []);

  useEffect(() => {
    if (!terminalMode) {
      requestAnimationFrame(() => adjustTextareaHeight());
    }
  }, [text, composerFocused, terminalMode, adjustTextareaHeight]);

  // Common post-send reset shared by all send paths (terminal, normal, and
  // attachment-confirmed). Kept as a stable closure-free helper so it stays
  // identical pre- and post-modal-confirm.
  const finalizeSend = () => {
    setText('');
    setMentionActive(false);
    if (taRef.current) {
      taRef.current.style.height = '';
    }
    setComposerFocused(false);
    // PR #1217 — clear the recording-origin pin AFTER every send. Without
    // this, a stale pin from a previous voice recording would route the NEXT
    // text-only send to the wrong session. The pin is re-set on the next
    // recording START (see start* handlers below).
    originSessionIdRef.current = null;
    // Tell BottomComposer to reset the userCollapsed guard so the next message
    // can auto-expand again on first keystroke.
    document.dispatchEvent(new CustomEvent('composer-text-cleared'));
  };

  const handleSendText = () => {
    if (terminalMode) {
      if (text.length === 0) return;
      // PR #1217 — route by origin pin if recording originated here; otherwise
      // active-tab fallback (terminal mode rarely cross-swipes mid-command).
      onSendText(text, originSessionIdRef.current);
      finalizeSend();
      return;
    }
    // BUG-NEW-1: drop rapid-fire send while the agent is still streaming.
    // Without this guard the backend `Still processing previous request` reply
    // surfaces as an inline error-pill in chat — jarring for the user. The
    // textarea is disabled while busy, but voice auto-submit + programmatic
    // composer-send-request paths can still reach here.
    if (busy) return;
    const val = text.trim();
    if (!val && !hasAttachments) return;
    // Confirmation guard for attachment-only sends. The send button (➤) appears
    // in the same screen position the mic button occupies otherwise, so muscle
    // memory aimed at the mic can land on send with empty text + an attachment.
    // Operator caught this twice on prod (2026-05-28). PR #1135 used
    // window.confirm() — issue #1137 replaces it with an in-app bottom sheet
    // (ConfirmAttachmentModal) so the surface matches the app's theming and
    // mobile UX rules (R-005 bottom sheet, R-004 semantic tokens, R-013 48dp
    // targets).
    if (!val && hasAttachments) {
      pendingAttachSendRef.current = val;
      setConfirmAttachOpen(true);
      return;
    }
    // PR #1217 — route by origin pin (record-START session) so a voice send
    // started on A but committed after a swipe to B still lands on A.
    onSendText(val, originSessionIdRef.current);
    finalizeSend();
  };

  // Resolved when the user taps "Send anyway" in ConfirmAttachmentModal.
  const handleConfirmAttachSend = () => {
    setConfirmAttachOpen(false);
    // PR #1217 — same origin-pin route as handleSendText so cross-swipe sends
    // land on the originating session.
    onSendText(pendingAttachSendRef.current, originSessionIdRef.current);
    pendingAttachSendRef.current = '';
    finalizeSend();
  };

  // Resolved when the user taps Cancel / ✕ / backdrop / Escape.
  const handleCancelAttachSend = () => {
    setConfirmAttachOpen(false);
    pendingAttachSendRef.current = '';
    // Return focus to the textarea so the user can dictate / type immediately,
    // satisfying the issue's "Cancel returns focus to textarea" acceptance.
    requestAnimationFrame(() => taRef.current?.focus());
  };

  // Keep a ref to the latest handleSendText so the auto-submit listener never goes stale
  const handleSendTextRef = useRef(handleSendText);
  useEffect(() => { handleSendTextRef.current = handleSendText; });

  // Auto-submit when a /plan execute command is routed to this session from another
  useEffect(() => {
    const handler = () => handleSendTextRef.current();
    document.addEventListener('auto-submit', handler);
    return () => document.removeEventListener('auto-submit', handler);
  }, []);

  // Whisper auto-submit (paired with handleWhisperRecorded above). When the
  // transcript callback sets pendingAutoSubmitVoiceRef.current=true and calls
  // setText(newText), this effect fires AFTER the render commits — by which
  // point handleSendTextRef.current closures over the new text. Race-free.
  //
  // inputbar-text-shadow-migration Phase 3: the dep was previously `[text]`
  // (the ACTIVE tab's draft selector). When the user swipes during the
  // transcription gap, the active-tab's draft never updates — the TRANSCRIPTION
  // wire-handler routes to the ORIGIN session's draft (see Scenario 2 +
  // useSocket.ts). The effect then silently disengaged. Re-keying off
  // `originDraft` (the origin session's draft) makes the race-handling fire
  // in the cross-tab case too. When origin == active tab, originDraft === text
  // and behavior is unchanged.
  useEffect(() => {
    if (pendingAutoSubmitVoiceRef.current && originDraft.trim()) {
      // Sync the pending-text ref to the live origin-session draft so
      // tryAutoSubmitVoice sees the freshest value. Then funnel through the
      // centralized helper.
      pendingAutoSubmitTextRef.current = originDraft;
      tryAutoSubmitVoice();
    }
  }, [originDraft, tryAutoSubmitVoice]);

  // Tab-switch survival (issue #1139). If the user backgrounds the tab between
  // mic-stop and TRANSCRIPTION arrival, Preact's render-then-effect chain can
  // stall in a hidden tab (rAF throttled, setTimeout coalesced). When the tab
  // becomes visible again — OR the page is about to be unloaded — we re-run
  // tryAutoSubmitVoice() to honor the send commitment the user made by tapping
  // stop. This is a belt-and-suspenders backstop; the microtask path in
  // handleWhisperRecorded handles the common case. Without this, the transcript
  // either vanished (cleared by socket-handler tab.inputDraft sync — PR #1109)
  // or stuck in the composer (auto-submit silently no-op'd).
  useEffect(() => {
    if (!autoSubmitVoice) return;
    const onVisible = () => {
      if (document.visibilityState === 'visible') {
        tryAutoSubmitVoice();
      }
    };
    const onPageHide = () => {
      // Last-ditch attempt on pagehide (Safari kills MediaRecorder + JS context
      // here). If a send is pending we fire synchronously; the socket emit is
      // best-effort but better than losing the transcript entirely.
      tryAutoSubmitVoice();
    };
    document.addEventListener('visibilitychange', onVisible);
    window.addEventListener('pageshow', onVisible);
    window.addEventListener('pagehide', onPageHide);
    return () => {
      document.removeEventListener('visibilitychange', onVisible);
      window.removeEventListener('pageshow', onVisible);
      window.removeEventListener('pagehide', onPageHide);
    };
  }, [autoSubmitVoice, tryAutoSubmitVoice]);

  // BottomComposer expanded footer → send / cancel via DOM events.
  // handleSendTextRef is always current. For cancel we use a stable ref too.
  const onCancelRef = useRef(onCancel);
  useEffect(() => { onCancelRef.current = onCancel; });

  useEffect(() => {
    const onSend = () => handleSendTextRef.current();
    const onCancelEvt = () => onCancelRef.current?.();
    document.addEventListener('composer-send-request', onSend);
    document.addEventListener('composer-cancel-request', onCancelEvt);
    return () => {
      document.removeEventListener('composer-send-request', onSend);
      document.removeEventListener('composer-cancel-request', onCancelEvt);
    };
  }, []);

  // BottomComposer collapse → re-run autoResize so textarea snaps back to compact height
  useEffect(() => {
    const handler = () => {
      requestAnimationFrame(() => {
        autoResizeTa();
        adjustTextareaHeight();
      });
    };
    document.addEventListener('composer-resize', handler);
    return () => document.removeEventListener('composer-resize', handler);
  // adjustTextareaHeight changes on composerFocused; we accept stale-capture here
  // because this fires only after collapse and the focused state is irrelevant.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleKeyDown = (e: KeyboardEvent) => {
    // @mention dropdown keyboard navigation
    if (mentionActive && mentionMatches.length > 0) {
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setMentionIndex((i) => (i + 1) % mentionMatches.length);
        return;
      }
      if (e.key === 'ArrowUp') {
        e.preventDefault();
        setMentionIndex((i) => (i - 1 + mentionMatches.length) % mentionMatches.length);
        return;
      }
      if (e.key === 'Enter' || e.key === 'Tab') {
        e.preventDefault();
        completeMention(mentionMatches[mentionIndex].keyword);
        return;
      }
      if (e.key === 'Escape') {
        e.preventDefault();
        setMentionActive(false);
        return;
      }
    }
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendText();
    }
  };

  // ── Hold-Space ownership state (#1657 Phase 5b — declared BEFORE
  // handleMicToggle so its no-op/error branches can release ownership).
  // holdInitiatedRef: the live recording (or in-flight start attempt) was
  // engaged by a Space hold. pendingHoldStopUntilRef: a hold was released
  // before mic init resolved — stop the moment recording engages (window-
  // bounded). releaseHoldOwnership(): the start attempt is dead or the
  // recording was stopped out-of-band — the hold owns NOTHING from here, so
  // a later keyup / the pending window must not stop anything (PR #1800
  // review finding 1: a stale pendingStop killed a click-started recording).
  const holdInitiatedRef = useRef(false);
  const pendingHoldStopUntilRef = useRef(0);
  const releaseHoldOwnership = () => {
    holdInitiatedRef.current = false;
    pendingHoldStopUntilRef.current = 0;
  };

  const handleMicToggle = async (e: Event) => {
    e.preventDefault();
    if (busy) return;

    // Another session holds the mic — refuse to start a second recording and
    // surface a brief hint. Safe to guard at the top: globalRecording is a
    // single slot, so if it's held elsewhere THIS session cannot be recording,
    // and we never block a legitimate stop on the holder.
    if (micHeldElsewhere) {
      haptic();
      setMicError('Mic is recording in another session');
      setTimeout(() => setMicError(null), 3000);
      return;
    }

    if (useWhisperPath) {
      // Whisper recording dock path (default on mobile, or when user hasn't opted out)
      if (dock.isRecording) {
        dock.stop();
        useComposerStore.getState().setGlobalRecording(null, null);
        // Whatever stopped this recording (hold keyup, mic click mid-hold,
        // pending-window resolution), the hold owns nothing past this point.
        releaseHoldOwnership();
      } else {
        haptic();
        setMicError(null);
        dictationBaseRef.current = textRef.current;
        // Pin the origin session at the moment of mic-tap so the eventual
        // transcription + auto-submit route to it, regardless of swipes.
        // Also publish to composerStore.globalRecording so OTHER session
        // dots can render the purple "recording" indicator.
        originSessionIdRef.current = currentSessionId ?? null;
        useComposerStore.getState().setGlobalRecording(currentSessionId ?? null, Date.now());
        // Real ptt gesture (coach-tooltip-system.md §5.2) — cancel the mic tip mid-
        // display + mark seen.
        coachMarkDiscovered('ptt');
        dock.start((errMsg) => {
          setMicError(errMsg);
          setTimeout(() => setMicError(null), 6000);
          // Mic init failed (e.g. permission denied) — release the global slot
          // and clear the origin pin so dots don't stay stuck on purple.
          useComposerStore.getState().setGlobalRecording(null, null);
          originSessionIdRef.current = null;
          // A hold-initiated attempt that never recorded: drop ownership +
          // any pending window so a stale hold flag can't stop a later
          // click-started recording.
          releaseHoldOwnership();
        });
      }
      return;
    }

    // Web Speech API path — desktop users who explicitly opted out of Whisper
    if ((forceWhisper || !phoneSupported) && transcribeAudio) {
      // Fallback to Whisper if Web Speech previously failed
      if (dock.isRecording) {
        dock.stop();
        useComposerStore.getState().setGlobalRecording(null, null);
        releaseHoldOwnership();
      } else {
        haptic();
        setMicError(null);
        dictationBaseRef.current = textRef.current;
        originSessionIdRef.current = currentSessionId ?? null;
        useComposerStore.getState().setGlobalRecording(currentSessionId ?? null, Date.now());
        coachMarkDiscovered('ptt');
        dock.start((errMsg) => {
          setMicError(errMsg);
          setTimeout(() => setMicError(null), 6000);
          // Mic init failed (e.g. permission denied) — release the global slot
          // and clear the origin pin so dots don't stay stuck on purple.
          useComposerStore.getState().setGlobalRecording(null, null);
          originSessionIdRef.current = null;
          releaseHoldOwnership();
        });
      }
      return;
    }

    if (!phoneSupported) {
      setMicError('Speech recognition not supported in this browser');
      setTimeout(() => setMicError(null), 4000);
      // No start is possible — a hold reaching this branch owns nothing.
      releaseHoldOwnership();
      return;
    }

    if (micPermission === 'denied') {
      setMicError('Mic blocked — in Chrome: tap ⋮ → Settings → Site settings → Microphone → Allow');
      setTimeout(() => setMicError(null), 8000);
      releaseHoldOwnership();
      return;
    }

    if (phoneListening) {
      stopPhone();
      releaseHoldOwnership();
    } else {
      haptic();
      setMicError(null);
      dictationBaseRef.current = textRef.current;
      coachMarkDiscovered('ptt');
      startPhone(
        (transcribed) => {
          const base = dictationBaseRef.current;
          const newText = base ? base + ' ' + transcribed : transcribed;
          setText(newText);
          setComposerFocused(true);
          requestAnimationFrame(() => taRef.current?.focus());
        },
        speechLang,
        (partial) => {
          const base = dictationBaseRef.current;
          setText(base ? base + ' ' + partial : partial);
        },
        (error) => {
          // Web Speech died mid/at-start — a hold-initiated attempt owns
          // nothing from here (mirror of the dock.start error callback).
          releaseHoldOwnership();
          if (transcribeAudio && (error.includes('internet') || error.includes('network') || error.includes('failed'))) {
            setForceWhisper(true);
            setMicError('Speech API unavailable — tap mic to use Whisper instead');
            setTimeout(() => setMicError(null), 4000);
            return;
          }
          setMicError(error);
          setTimeout(() => setMicError(null), 4000);
        },
      );
    }
  };

  const isListening = phoneListening || dock.isRecording;

  // Native Web-Speech PTT publishes mic-hot state to the global recording SoT so the
  // self-echo suppression (App.tsx / ReaderOverlay) covers this path too. The Whisper
  // path sets globalRecording imperatively in handleMicToggle, so skip it here. Native
  // Web Speech has no echoCancellation, so this path needs the guard most.
  useEffect(() => {
    if (useWhisperPath) return; // Whisper path owns globalRecording
    if (phoneListening) {
      useComposerStore.getState().setGlobalRecording(currentSessionId ?? null, Date.now());
    } else {
      useComposerStore.getState().setGlobalRecording(null, null);
    }
  }, [phoneListening, useWhisperPath, currentSessionId]);

  // ── Desktop Hold-Space push-to-talk (#1657 Phase 5b) ──────────────────────
  // `ptt-hold-start` / `ptt-hold-end` are transient VIEW-LAYER triggers
  // dispatched on `document` by desktop/HoldToTalkControl (nothing dispatches
  // them on mobile — these listeners are inert there). They route to the SAME
  // handleMicToggle the rendered mic control uses (whisper PTTButton path or
  // Web Speech toggle — recorder hooks stay shared per plan §3.2), through the
  // pure decideHoldStart/decideHoldEnd routing (utils/holdToTalk.ts):
  //   - hold-start while a recording is live (click-started) is NOT adopted;
  //   - hold-start while busy / mic-held-elsewhere never takes ownership —
  //     handleMicToggle would no-op, and a spuriously-armed pendingStop could
  //     later kill a CLICK-started recording (PR #1800 review finding 1);
  //   - hold-end only stops what the hold itself started (a stray Space keyup
  //     never stops a click-started recording);
  //   - hold-end BEFORE mic init resolved (quick tap — dock.start's
  //     getUserMedia is async) arms a short pending-stop window; the effect on
  //     isListening below stops the instant recording engages, so a tap can't
  //     leave the mic silently hot;
  //   - start attempts that die WITHOUT recording (permission denied, Web
  //     Speech unsupported/error) release ownership inside handleMicToggle
  //     (releaseHoldOwnership above) — same reason.
  // Same blessed CustomEvent bucket as `focus-composer` / `auto-submit`
  // (R-A2 view-trigger exemption) — recording STATE still lives only in the
  // dock + composerStore.globalRecording.
  const isListeningRef = useRef(isListening);
  isListeningRef.current = isListening;
  const busyRef = useRef(busy);
  busyRef.current = busy;
  const micHeldElsewhereRef = useRef(micHeldElsewhere);
  micHeldElsewhereRef.current = micHeldElsewhere;
  const handleMicToggleRef = useRef(handleMicToggle);
  handleMicToggleRef.current = handleMicToggle;
  useEffect(() => {
    const onHoldStart = (e: Event) => {
      pendingHoldStopUntilRef.current = 0;
      const d = decideHoldStart(
        isListeningRef.current,
        busyRef.current,
        micHeldElsewhereRef.current,
      );
      holdInitiatedRef.current = d.holdInitiated;
      if (d.toggle) handleMicToggleRef.current(e);
    };
    const onHoldEnd = (e: Event) => {
      const d = decideHoldEnd(holdInitiatedRef.current, isListeningRef.current);
      holdInitiatedRef.current = d.holdInitiated;
      if (d.pendingStop) {
        pendingHoldStopUntilRef.current = Date.now() + PENDING_HOLD_STOP_WINDOW_MS;
        return;
      }
      if (d.toggle) handleMicToggleRef.current(e);
    };
    document.addEventListener(PTT_HOLD_START, onHoldStart);
    document.addEventListener(PTT_HOLD_END, onHoldEnd);
    return () => {
      document.removeEventListener(PTT_HOLD_START, onHoldStart);
      document.removeEventListener(PTT_HOLD_END, onHoldEnd);
    };
  }, []);
  // Quick-tap race resolution: recording engaged AFTER the hold was already
  // released → stop immediately (within the pending window only, so a later
  // click-started recording is never wrongly stopped by a stale flag).
  useEffect(() => {
    if (!isListening) return;
    if (pendingHoldStopUntilRef.current === 0) return;
    const withinWindow = Date.now() <= pendingHoldStopUntilRef.current;
    pendingHoldStopUntilRef.current = 0;
    if (withinWindow) handleMicToggleRef.current(new CustomEvent(PTT_HOLD_END));
  }, [isListening]);

  const isTranscribing = whisperTranscribing;
  const hasText = terminalMode ? text.length > 0 : text.trim().length > 0;
  const micBlocked = micPermission === 'denied';
  const micCls = `ptt-button compact${isListening ? ' recording' : ''}${isTranscribing ? ' transcribing' : ''}${micBlocked ? ' mic-blocked' : ''} phone-mic`;

  const handleMicPointerDown = () => {
    if (!onToggleLang) return;
    micIsLongPress.current = false;
    micLongPressTimer.current = setTimeout(() => {
      micIsLongPress.current = true;
      // 400ms after pointerdown — safe to setState, outside pointer event window
      setTimeout(() => onToggleLang(), 0);
      haptic();
    }, 400);
  };
  const handleMicPointerUp = () => {
    if (micLongPressTimer.current) { clearTimeout(micLongPressTimer.current); micLongPressTimer.current = null; }
  };

  const langLabel = speechLang === 'bg-BG' ? 'BG' : speechLang ? 'EN' : null;

  const commonTextProps = {
    placeholder: busy
      ? 'Working…'
      : whisperTranscribing
        ? 'Transcribing…'
        : (phoneListening || dock.isRecording)
          ? 'Listening…'
          : (placeholder || 'Type a message…'),
    value: text,
    disabled: busy, // phone listening: keep editable for interim display
    enterkeyhint: 'send' as const,
  };

  const handleCancel = useCallback((e: Event) => {
    e.preventDefault();
    e.stopPropagation();
    onCancel?.();
  }, [onCancel]);

  // R-001 (UI/UX 2026 runbook revised, Phase 2.5-F): during Whisper recording
  // or transcription, the textarea is unmounted and the PTT dock expands to
  // occupy its slot in the BottomComposer bar. CSS rules at
  // `frontend/src/style.css` § "D1-001" drive the dock's full-width expansion;
  // this flag is the runtime trigger. The waveform IS the input — voice reads
  // as first-class, not as augmenting text. Pre-recording draft is preserved
  // in state (`text`) and re-rendered when recording ends.
  const isRecordingActive = dock.isRecording || whisperTranscribing;

  return (
    <div class={`input-bar${busy ? ' input-bar-busy' : ''}${!terminalMode && composerFocused ? ' input-bar--composer-expanded' : ''}${isRecordingActive ? ' input-bar--recording' : ''}`} style={{ position: 'relative' }}>
      {/* CS-3 (plan §9.3): minimal user-visible "turn failed" notice — the composer no
          longer un-sticks silently. Honest copy (send_failed carries no text, so no
          re-send is promised) + a dismiss. Conditionally-rendered sibling: adds no
          existing-DOM/classname change (the frozen mobile-ux suite asserts against those). */}
      {!terminalMode && sendFailedReason && currentSessionId && (
        <div class="composer-send-failed" role="status">
          <span class="composer-send-failed__msg">
            {sendFailedReason === 'stream_stalled'
              ? 'The response stalled and was stopped. You can send another message.'
              : "The last message didn't complete. You can send another message."}
          </span>
          <button
            type="button"
            class="composer-send-failed__dismiss"
            aria-label="Dismiss"
            onClick={() => useComposerStore.getState().dismissSendFailed(currentSessionId)}
          >
            ✕
          </button>
        </div>
      )}
      {/* @mention autocomplete dropdown */}
      {mentionActive && mentionMatches.length > 0 && (
        <div class="mention-dropdown">
          {mentionMatches.map((m, i) => (
            <div
              key={m.keyword}
              class={`mention-item${i === mentionIndex ? ' mention-item--active' : ''}`}
              onMouseDown={(e) => { e.preventDefault(); completeMention(m.keyword); }}
              onMouseEnter={() => setMentionIndex(i)}
            >
              <span class={`mention-item-icon ${m.iconCls}`}>{m.icon}</span>
              <div class="mention-item-info">
                <div class="mention-item-name">{m.keyword}</div>
                <div class="mention-item-desc">{m.desc}</div>
              </div>
            </div>
          ))}
        </div>
      )}
      {/* Button column: mic on top, attach below */}
      <div class="input-bar-actions">
        {busy ? (
          <button
            class="cancel-btn"
            onClick={handleCancel}
            onTouchEnd={handleCancel}
            title="Cancel"
            aria-label="Cancel"
          >
            {voiceState ? <CancelIcon /> : '✕'}
          </button>
        ) : (
          <>
            {/* Quote chip — fallback for when floating QuotePill is off-screen or dismissed */}
            {quoteSelection && !busy && (
              <button
                class="quote-chip"
                onPointerDown={(e) => {
                  e.preventDefault();
                  executeQuote(quoteSelection.text, quoteSelection.sourceEl, currentSessionId ?? null);
                  setQuoteSelection(null);
                }}
                title="Quote selected text into compose area"
                aria-label="Quote selected text"
              >
                ❝
              </button>
            )}
            {useWhisperPath ? (
              <PTTButton
                busy={false}
                isRecording={dock.isRecording}
                isLocked={dock.isLocked}
                durationMs={dock.durationMs}
                levelBars={dock.levelBars}
                atSoftCap={dock.atSoftCap}
                transcribing={whisperTranscribing}
                onMicToggle={handleMicToggle}
                onLock={dock.lock}
                onCancelRecording={() => { dock.cancel(); setWhisperTranscribing(false); }}
                onExtendCap={dock.extendCap}
                micError={micError}
                heldElsewhere={micHeldElsewhere}
                voiceState={voiceState}
              />
            ) : (
              // data-coach-id="mic" anchors the (dormant) ptt coach popover to the native
              // mic. TODO(coach Phase 4): the whisper-path <PTTButton/> above is the common
              // mobile mic — tag its idle root with data-coach-id="mic" too when wiring ptt.
              // Desktop voice chrome (#2204): with `voiceState` present the wrap becomes the
              // composer mic cluster — data-voice-state + the ring/bars + the visible state
              // label render the SAME machine the home hero shows (DesktopShell derives the
              // state; this is a pure renderer). The machine's own guards are untouched:
              // held-elsewhere still disables + explains, transcribing still disables.
              <div
                class={`mic-wrap${voiceState ? ' desktop-composer-mic' : ''}`}
                data-voice-state={voiceState}
              >
                <button
                  class={`${micCls}${micHeldElsewhere ? ' mic-btn--held' : ''}`}
                  data-coach-id="mic"
                  onClick={handleMicToggle}
                  onPointerDown={handleMicPointerDown}
                  onPointerUp={handleMicPointerUp}
                  onPointerCancel={handleMicPointerUp}
                  disabled={isTranscribing || micHeldElsewhere}
                  aria-label="Dictate"
                  title={micHeldElsewhere ? 'Microphone is recording in another session' : micBlocked ? 'Microphone blocked — tap for instructions' : isTranscribing ? 'Transcribing...' : isListening ? 'Tap to stop' : onToggleLang ? 'Tap to dictate · Hold to switch language' : 'Tap to dictate'}
                >
                  {voiceState ? (
                    micBlocked ? <MicBlockedIcon /> : <MicIcon />
                  ) : (
                    micBlocked ? '🚫' : isTranscribing ? '⏳' : isListening ? '⏹' : '🎙'
                  )}
                  {voiceState && <span class="desktop-composer-mic-ring" aria-hidden="true" />}
                  {voiceState && (
                    <span class="desktop-composer-mic-bars" aria-hidden="true">
                      <span class="desktop-composer-mic-bar" />
                      <span class="desktop-composer-mic-bar" />
                      <span class="desktop-composer-mic-bar" />
                    </span>
                  )}
                </button>
                {voiceState && (
                  <span class="desktop-composer-mic-label" role="status" aria-live="polite">
                    {voiceStateLabel(voiceState)}
                  </span>
                )}
                {langLabel && <span class="mic-lang-badge">{langLabel}</span>}
              </div>
            )}
            {onAttachTap && !hideAttach && (
              <button
                class={`attach-btn${hasAttachments ? ' has-attachments' : ''}`}
                onClick={onAttachTap}
                aria-label="Attach image"
                title="Attach image"
              >
                {voiceState ? <AttachIcon /> : '\uD83D\uDCCE'}
              </button>
            )}
            <button
              class="catchup-btn"
              onClick={() => {
                if (currentSessionId) useComposerStore.getState().appendOrReplaceDraft(currentSessionId, CATCHUP_PROMPT, 'replace');
                document.dispatchEvent(new CustomEvent('focus-composer'));
              }}
              aria-label="Catch me up"
              title="Catch me up — inject a context-refresh prompt"
            >
              {voiceState ? <CatchupIcon /> : '↻'}
            </button>
          </>
        )}
      </div>
      {/* Textarea spanning full height next to buttons.
          R-001: hidden during recording so the PTT dock / waveform IS the input.
          Pre-recording draft is preserved in state and re-rendered when recording ends. */}
      {!isRecordingActive && (terminalMode ? (
        <input
          ref={inputRef}
          class={`text-input${busy ? ' text-input-busy' : ''} terminal-input`}
          type="text"
          {...commonTextProps}
          onInput={(e) => {
            const v = (e.target as HTMLInputElement).value;
            setText(v);
          }}
          onKeyDown={handleKeyDown}
          {...{
            autocapitalize: 'off',
            autocorrect: 'off',
            autocomplete: 'off',
            spellcheck: false,
            inputMode: 'text' as const,
            'data-gramm': 'false',
          }}
        />
      ) : (
        <textarea
          ref={taRef}
          class={`text-input text-input--textarea${busy ? ' text-input-busy' : ''}`}
          rows={1}
          {...commonTextProps}
          onInput={(e) => {
            const el = e.target as HTMLTextAreaElement;
            const v = el.value;
            setText(v);
            updateMentionState(v);
            autoResizeTa();
            // Auto-expand on first character typed (Phase 4 splash UX).
            // Request fires only when NOT already expanded, so typing inside
            // the expanded view doesn't re-fire the event.
            if (v.length > 0 && !el.closest('.bottom-composer--expanded')) {
              document.dispatchEvent(new CustomEvent('composer-expand-request'));
            }
          }}
          onPaste={(e) => {
            // Expand immediately when pasted text would push total length over threshold.
            const pasted = (e as unknown as ClipboardEvent).clipboardData?.getData('text') ?? '';
            const total = (taRef.current?.value ?? '').length + pasted.length;
            if (total >= EXPAND_PASTE_THRESHOLD && !taRef.current?.closest('.bottom-composer--expanded')) {
              // Allow the paste to land first, then request expansion.
              requestAnimationFrame(() => document.dispatchEvent(new CustomEvent('composer-expand-request')));
            }
          }}
          onKeyDown={handleKeyDown}
          onFocus={() => setComposerFocused(true)}
          onBlur={() => {
            // Delay to allow mention item clicks to register
            setTimeout(() => {
              if (!mentionActive) {
                const v = taRef.current?.value?.trim() ?? '';
                if (!v) setComposerFocused(false);
              }
            }, 150);
          }}
        />
      ))}
      {/* Send button on the right edge */}
      {!busy && (hasText || hasAttachments) && (
        <button
          class="send-btn"
          onClick={handleSendText}
          title="Send"
          aria-label={voiceState ? 'Send' : undefined}
        >
          {voiceState ? <SendIcon /> : '➤'}
        </button>
      )}
      {/* Floating mic-error toast migrated to the coach substrate (Phase 3b) — see the
          micError-mirror effect above. PTTButton's inline .ptt-hint--error still reads
          micError directly (a distinct, permanent recording-dock rendering). */}
      {/* Empty-text + attachment send guard — issue #1137 / supersedes #1135. */}
      <ConfirmAttachmentModal
        open={confirmAttachOpen}
        onConfirm={handleConfirmAttachSend}
        onCancel={handleCancelAttachSend}
      />
    </div>
  );
}
